import { useState, useEffect, createContext, useContext } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, LogOut, Menu, X, ChevronsUpDown, ChevronDown, Sun, Moon } from 'lucide-react';
import { useTheme } from '../../hooks/useTheme.jsx';
import { cn } from '@/lib/utils';

const SidebarContext = createContext(null);

export function useSidebar() {
  return useContext(SidebarContext);
}

function SidebarNavItem({ item, collapsed, onNavigate }) {
  const location = useLocation();
  const isActive = item.end
    ? location.pathname === item.to
    : location.pathname.startsWith(item.to) && (item.to !== '/' || location.pathname === '/');

  return (
    <NavLink
      to={item.to}
      onClick={onNavigate}
      data-tour={item['data-tour'] || undefined}
      className={cn(
        'flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] font-medium transition-all duration-150',
        collapsed && 'justify-center px-2',
        isActive
          ? 'bg-primary text-primary-foreground shadow-sm'
          : 'text-muted-foreground hover:bg-accent hover:text-foreground'
      )}
    >
      {item.icon && (
        <item.icon
          className={cn('h-[18px] w-[18px] shrink-0', isActive ? 'text-primary-foreground' : 'text-muted-foreground')}
          strokeWidth={1.8}
        />
      )}
      {!collapsed && <span className="truncate">{item.label}</span>}
      {!collapsed && item.badge != null && (
        <span className={cn(
          'ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-bold',
          isActive ? 'bg-white/25 text-primary-foreground' : 'bg-profun-pink/10 text-profun-pink'
        )}>
          {item.badge}
        </span>
      )}
    </NavLink>
  );
}

export default function Sidebar({
  navSections = [],
  brandName = 'KSA ProHR',
  brandSubtitle,
  userInfo,
  onLogout,
  storageKey = 'sidebar-collapsed',
  showWorkspaceSwitcher = false,
  isAdmin = false,
  aboveUserSlot,
}) {
  const { isDark, toggle: toggleTheme } = useTheme();
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(storageKey) === 'true'; } catch { return false; }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isOnAdmin = location.pathname.startsWith('/admin');

  useEffect(() => {
    try { localStorage.setItem(storageKey, String(collapsed)); } catch {}
  }, [collapsed, storageKey]);

  // Per-section collapse state (keyed by section label), persisted separately
  // from the whole-sidebar collapse. Default: expanded. A section that
  // contains the active route always renders open regardless of the toggle.
  const sectionsKey = `${storageKey}:sections`;
  const [closedSections, setClosedSections] = useState(() => {
    try { return JSON.parse(localStorage.getItem(sectionsKey) || '{}') || {}; } catch { return {}; }
  });
  useEffect(() => {
    try { localStorage.setItem(sectionsKey, JSON.stringify(closedSections)); } catch {}
  }, [closedSections, sectionsKey]);
  const toggleSection = (label) =>
    setClosedSections((prev) => ({ ...prev, [label]: !prev[label] }));
  const sectionHasActive = (section) =>
    (section.items || []).some((item) =>
      item.end ? location.pathname === item.to
        : location.pathname.startsWith(item.to) && (item.to !== '/' || location.pathname === '/'));

  const sidebarContent = (
    <div className="flex h-full flex-col">
      {/* Brand */}
      <div className={cn(
        'flex items-center px-4 h-14 shrink-0',
        collapsed ? 'justify-center' : 'gap-3'
      )}>
        <img src="/profun-logo.svg" alt="ProFun" className="h-7 w-7 shrink-0" />
        {!collapsed && (
          <span className="text-[15px] font-bold tracking-tight text-foreground">{brandName}</span>
        )}
      </div>

      {/* Workspace switcher */}
      {!collapsed && showWorkspaceSwitcher && isAdmin && (
        <div className="relative px-3 pb-3">
          <button
            onClick={() => setSwitcherOpen(!switcherOpen)}
            className="flex w-full items-center justify-between rounded-lg bg-secondary px-3 py-2 text-[13px] font-medium text-secondary-foreground transition-colors hover:bg-accent"
          >
            <span>{isOnAdmin ? 'Admin Panel' : 'Employee View'}</span>
            <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
          {switcherOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setSwitcherOpen(false)} />
              <div className="absolute left-3 right-3 top-full z-50 mt-1 overflow-hidden rounded-lg border border-border bg-popover shadow-lg">
                <button
                  onClick={() => { navigate('/'); setSwitcherOpen(false); }}
                  className={cn(
                    'flex w-full items-center px-3 py-2.5 text-[13px] text-left transition-colors hover:bg-accent',
                    !isOnAdmin && 'font-semibold text-primary'
                  )}
                >
                  Employee View
                </button>
                <button
                  onClick={() => { navigate('/admin'); setSwitcherOpen(false); }}
                  className={cn(
                    'flex w-full items-center px-3 py-2.5 text-[13px] text-left transition-colors hover:bg-accent border-t border-border',
                    isOnAdmin && 'font-semibold text-primary'
                  )}
                >
                  Admin Panel
                </button>
              </div>
            </>
          )}
        </div>
      )}

      <div className="mx-3 h-px bg-border" />

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-3">
        {navSections.map((section, si) => {
          // A labeled section is collapsible when the sidebar is expanded.
          // It renders open unless the user closed it AND it doesn't contain
          // the active route. Unlabeled sections + collapsed sidebar: always open.
          const collapsible = !!section.label && !collapsed;
          const isOpen = !collapsible || !closedSections[section.label] || sectionHasActive(section);
          return (
            <div key={si} className={cn(si > 0 && 'mt-5')}>
              {section.label && !collapsed && (
                <button
                  type="button"
                  onClick={() => toggleSection(section.label)}
                  className="mb-2 flex w-full items-center justify-between px-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground transition-colors hover:text-foreground"
                >
                  <span>{section.label}</span>
                  <ChevronDown className={cn('h-3.5 w-3.5 transition-transform duration-150', !isOpen && '-rotate-90')} />
                </button>
              )}
              {collapsed && si > 0 && (
                <div className="mx-1 mb-3 h-px bg-border" />
              )}
              {isOpen && (
                <ul className="space-y-0.5">
                  {section.items.map((item) => (
                    <li key={item.to}>
                      <SidebarNavItem
                        item={item}
                        collapsed={collapsed}
                        onNavigate={() => setMobileOpen(false)}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </nav>

      {/* Above-username slot (e.g. the What's New banner). Rendered raw so
          the slot component owns its own spacing + visibility — nothing
          (no empty bordered divider) shows when it has nothing to display. */}
      {aboveUserSlot && (typeof aboveUserSlot === 'function' ? aboveUserSlot({ collapsed }) : aboveUserSlot)}

      {/* User */}
      <div className="shrink-0 border-t border-border px-3 py-2.5">
        {userInfo && (
          <div className={cn('flex items-center', collapsed ? 'justify-center' : 'gap-2.5')}>
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-bold text-primary">
              {userInfo.initials || userInfo.name?.charAt(0) || '?'}
            </div>
            {!collapsed && (
              <>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13px] font-medium text-foreground">{userInfo.name}</div>
                  <div className="truncate text-[11px] text-muted-foreground">{userInfo.email}</div>
                </div>
                <button
                  onClick={toggleTheme}
                  className="rounded-md p-1.5 text-muted-foreground transition-all hover:bg-accent hover:text-foreground"
                  title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                  aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {isDark ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
                </button>
                {onLogout && (
                  <button
                    onClick={onLogout}
                    className="rounded-md p-1.5 text-muted-foreground transition-all hover:bg-accent hover:text-foreground"
                    title="Sign out"
                  >
                    <LogOut className="h-3.5 w-3.5" />
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Collapse toggle */}
      <button
        onClick={() => setCollapsed(c => !c)}
        className="hidden items-center justify-center border-t border-border py-2 text-muted-foreground hover:bg-accent hover:text-foreground lg:flex"
      >
        {collapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronLeft className="h-3.5 w-3.5" />}
      </button>
    </div>
  );

  return (
    <SidebarContext.Provider value={{ collapsed, setCollapsed }}>
      {/* Mobile hamburger */}
      <button
        onClick={() => setMobileOpen(o => !o)}
        className="fixed left-4 top-4 z-50 flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-card shadow-sm lg:hidden"
      >
        {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
      </button>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-foreground/10 backdrop-blur-sm lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      <aside
        className={cn(
          'fixed left-0 top-0 z-40 h-full border-r border-border bg-card transition-all duration-200',
          'hidden lg:flex lg:flex-col',
          collapsed ? 'lg:w-[60px]' : 'lg:w-[240px]',
        )}
      >
        {sidebarContent}
      </aside>

      <aside className={cn(
        'fixed left-0 top-0 z-40 flex h-full w-[240px] flex-col border-r border-border bg-card shadow-xl transition-transform duration-200 lg:hidden',
        mobileOpen ? 'translate-x-0' : '-translate-x-full'
      )}>
        {sidebarContent}
      </aside>
    </SidebarContext.Provider>
  );
}
